import type { CustomField, UUID } from "@situm/sdk-js";
import type {
  Feature,
  FeatureCollection,
  LineString,
  Point,
  Polygon,
} from "geojson";

import type { UndoableState } from "@/app/libs/undoable";
import type { DeepPartial } from "@/features/utils/genericity";
import type { GeofenceGeojsonProperties } from "@/libs/models/geofence";
import type { PathDictGenericItem, PathDictNodeItem } from "@/libs/models/path";
import type { ConfigState } from "../config/types";

/*************************************************/
/* Redux slice */
/*************************************************/
export interface SliceProps {
  filterText: string | null;
  profiles: {
    list: Profile[];
    newName: string | null;
  };
  overlaysEditor: {
    selectedFeatureId?: string;
    visible: Record<OverlayFeatureTypes, boolean>;
    formErrors: Record<string, string | null>;
  };
  pathsEditor: {
    changesHistory: UndoableState<EditorChanges<PathDictGenericItem>>;
    selectedFeatureId?: string;
    temporaryNode?: {
      position: { lat: number; lng: number };
      connectingNodeId?: string;
    };
    settings: {
      analyzeProblematicNodes: boolean;
      displayNodeIds: boolean;
      displayOverGeojson: boolean;
      displayFloorChanges: boolean;
    };
    tool: "select" | "draw_paths" | "batch_drag";
  };
  changes: {
    overlayFeatures: EditorChanges<GenericOverlayFeature>;
  };
}

/*************************************************/
/* Overlay features */
/*************************************************/
export enum OverlayFeatureTypes {
  icon = "icon",
  model = "model",
  text = "text",
  poi = "poi",
  geofence = "geofence",
}

/*************************************************/
/* Editor changes */
/*************************************************/
type EditorChangeType = "new" | "updated" | "deleted";
export type EditorChange<T> = {
  [K in EditorChangeType]: {
    type: K;
  } & (K extends "new"
    ? { item: T }
    : K extends "updated"
      ? { item: DeepPartial<T> }
      : { item?: never });
}[EditorChangeType];
export type EditorChanges<T> = Record<string, EditorChange<T>>;

/*************************************************/
/* Paths */
/*************************************************/
type ProblemType = "isolated" | "near" | "outside";
export type PathProblem = PathDictNodeItem & { problemType: ProblemType };

/*************************************************/
/* Common properties for all overlay types */
/*************************************************/
export type OverlayFeatureBaseProperties = {
  id: string | number;
  type: OverlayFeatureTypes;
  name: string;
  floorId?: number;
  floorLevel?: number;
  updatedAt?: EpochTimeStamp;
  createdAt?: EpochTimeStamp;
};
type RotatableFeature = {
  orientation: [number, number, number];
};
type ScalableFeature = {
  scale: number;
};
export enum TextAlignmentOptions {
  start = "start",
  middle = "middle",
  end = "end",
}

/*************************************************/
/* Specific properties for each overlay type */
/*************************************************/
type ModelProperties = OverlayFeatureBaseProperties &
  RotatableFeature &
  ScalableFeature & {
    type: OverlayFeatureTypes.model;
    url: string;
    categoryIds?: number[];

    // visibility and zoom limits (nullable = no limit)
    isOutdoor?: boolean;
    minZoom?: number | null;
    maxZoom?: number | null;
    drawShadow?: boolean | null;
  };

export type IconProperties = OverlayFeatureBaseProperties &
  RotatableFeature &
  ScalableFeature & {
    type: OverlayFeatureTypes.icon;
    url: string;
    billboard?: boolean;
    poiId?: number;
    iconDimensions?: [number, number];
    categoryIds?: number[];
  };

type TextProperties = OverlayFeatureBaseProperties &
  RotatableFeature &
  ScalableFeature & {
    type: OverlayFeatureTypes.text;
    text: string;
    textColor: string;
    fontFamily: string;
    billboard?: boolean;
    poiId?: number;
    textAlignment?: TextAlignmentOptions;
    categoryIds?: number[];
  };

type PoiProperties = OverlayFeatureBaseProperties & {
  type: OverlayFeatureTypes.poi;
  name: string;
  floorId: number;
  categoryId: number;
  categoryIds: number[];
  info: string;
  buildingId: number;
  customFields: CustomField[];
  icon: string | null;
  selectedIcon: string | null;
  matchedGeofenceId: string | null;
};

/*************************************************/
/* Geojson Feature types */
/*************************************************/
// Generic geometry type to allow for empty coordinates
export type GeoJsonGeometry<T extends "Point" | "Polygon" | "LineString"> =
  T extends "Point"
    ? Omit<Point, "coordinates"> & { coordinates: Coordinate | [] }
    : T extends "Polygon"
      ? Omit<Polygon, "coordinates"> & { coordinates: Coordinate[][] | [] }
      : T extends "LineString"
        ? Omit<LineString, "coordinates"> & { coordinates: Coordinate[] | [] }
        : never;

export type GeofenceOverlayFeature = Feature<GeoJsonGeometry<"Polygon">> & {
  id: string;
  properties: GeofenceGeojsonProperties;
};
export type ModelOverlayFeature = Feature<GeoJsonGeometry<"Point">> & {
  id: string;
  properties: ModelProperties;
};
export type IconOverlayFeature = Feature<GeoJsonGeometry<"Point">> & {
  id: string;
  properties: IconProperties;
};
export type TextOverlayFeature = Feature<GeoJsonGeometry<"Point">> & {
  id: string;
  properties: TextProperties;
};
export type PoiOverlayFeature = Feature<GeoJsonGeometry<"Point">> & {
  id: string;
  properties: PoiProperties;
};

export type GenericOverlayFeature =
  | GeofenceOverlayFeature
  | ModelOverlayFeature
  | IconOverlayFeature
  | TextOverlayFeature
  | PoiOverlayFeature;

export type OverlayFeatureCollection<
  T extends GenericOverlayFeature = GenericOverlayFeature,
> = FeatureCollection<T["geometry"], T["properties"]> & {
  features: T[];
};

export interface Profile {
  id: UUID;
  name: string;
  organizationId?: UUID;
  content: Partial<ConfigState>;
  metadata?: {
    temporal: boolean;
    clonedId?: UUID;
  };
}
